import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wallet, ArrowDownRight, Clock, Link2, Copy, Check, Pencil, Eye, Users as UsersIcon, TrendingUp, Star } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";


export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [editingOrg, setEditingOrg] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await api.get(`/user/dashboard`);
      setDashboard(data);
      setOrgName(data.organization_name || "");
    } catch {
      toast.error("Failed to load dashboard");
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    // Fetch analytics
    const fetchAnalytics = async () => {
      try {
        const { data } = await api.get(`/user/analytics`);
        setAnalytics(data);
      } catch {}
    };
    fetchAnalytics();
  }, [fetchDashboard]);

  const handleWithdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setWithdrawLoading(true);
    try {
      const { data } = await api.post(`/user/withdraw`, { amount: amt });
      toast.success(data.message);
      setWithdrawOpen(false);
      setAmount("");
      fetchDashboard();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Withdrawal failed");
    } finally {
      setWithdrawLoading(false);
    }
  };

  const balance = dashboard?.balance ?? user?.balance ?? 0;
  const uniqueLink = dashboard?.unique_link || user?.unique_link || "";
  const publicUrl = uniqueLink ? `${window.location.origin}/s/${uniqueLink}` : "";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveOrg = async () => {
    setOrgLoading(true);
    try {
      await api.put(`/user/settings`, { organization_name: orgName });
      toast.success("Organization name updated");
      setEditingOrg(false);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setOrgLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 md:py-12 animate-fade-in" data-testid="dashboard-page">
      <div className="mb-8">
        <p className="text-xs font-medium tracking-widest uppercase text-emerald-600 mb-2 animate-fade-in-up stagger-1">Overview</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight animate-fade-in-up stagger-2" data-testid="dashboard-title">
          Dashboard
        </h1>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Balance Hero Card */}
        <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="balance-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium tracking-wide uppercase text-muted-foreground mb-2">
                  Available Balance
                </p>
                <p className="font-heading text-4xl sm:text-5xl font-black tracking-tighter" data-testid="user-balance-display">
                  <span className="gradient-text">${balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-sm bg-emerald-50 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <Button
              className="mt-6 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setWithdrawOpen(true)}
              data-testid="withdraw-open-btn"
            >
              <ArrowDownRight className="mr-2 h-4 w-4" /> Withdraw
            </Button>
          </CardContent>
        </Card>

        {/* Points Card */}
        <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="points-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium tracking-wide uppercase text-muted-foreground mb-2">
                  Points
                </p>
                <p className="font-heading text-4xl font-black tracking-tighter" data-testid="user-points-display">
                  {(dashboard?.points ?? 0).toLocaleString()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-sm bg-amber-50 flex items-center justify-center">
                <Star className="h-6 w-6 text-amber-500" />
              </div>
            </div>
            {analytics?.points_breakdown && (
              <div className="mt-4 pt-4 border-t border-border space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Tier 1 visits</span>
                  <span className="font-medium">{analytics.points_breakdown.tier_1.visits} ({analytics.points_breakdown.tier_1.points} pts)</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Tier 2 visits</span>
                  <span className="font-medium">{analytics.points_breakdown.tier_2.visits} ({analytics.points_breakdown.tier_2.points} pts)</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Tier 3 visits</span>
                  <span className="font-medium">{analytics.points_breakdown.tier_3.visits} ({analytics.points_breakdown.tier_3.points} pts)</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Info */}
        <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6 md:p-8">
            <p className="text-sm font-medium tracking-wide uppercase text-muted-foreground mb-2">Account</p>
            <p className="font-heading text-lg font-semibold mb-1">{dashboard?.name || user?.name}</p>
            <p className="text-sm text-muted-foreground">{dashboard?.email || user?.email}</p>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                <Clock className="inline h-3.5 w-3.5 mr-1" />
                {dashboard?.withdrawals?.length || 0} withdrawals
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unique Link & Organization */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="unique-link-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="h-4 w-4 text-emerald-600" />
              <p className="text-sm font-medium tracking-wide uppercase text-muted-foreground">Your Unique Link</p>
            </div>
            {uniqueLink ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-muted px-3 py-2 rounded-sm truncate" data-testid="unique-link-display">
                  {publicUrl}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm shrink-0"
                  onClick={handleCopyLink}
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No link generated yet</p>
            )}
            <p className="text-xs text-muted-foreground mt-2">Share this link to display your support page</p>
          </CardContent>
        </Card>

        <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="org-name-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium tracking-wide uppercase text-muted-foreground">Organization Name</p>
              {!editingOrg && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingOrg(true)}
                  data-testid="edit-org-btn"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            {editingOrg ? (
              <div className="flex items-center gap-2">
                <Input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Enter organization name"
                  className="flex-1"
                  data-testid="org-name-input"
                />
                <Button
                  size="sm"
                  className="rounded-sm"
                  onClick={handleSaveOrg}
                  disabled={orgLoading}
                  data-testid="save-org-btn"
                >
                  {orgLoading ? "..." : "Save"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  onClick={() => { setEditingOrg(false); setOrgName(dashboard?.organization_name || ""); }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <p className="text-base font-medium" data-testid="org-name-display">
                {orgName || <span className="text-muted-foreground italic">Not set</span>}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">Displayed on your public support page</p>
          </CardContent>
        </Card>
      </div>

      {/* Withdrawal History */}
      <Card className="border-border rounded-sm shadow-sm bg-white/80 backdrop-blur-sm" data-testid="withdrawals-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">Withdrawal History</CardTitle>
        </CardHeader>
        <CardContent>
          {!dashboard?.withdrawals?.length ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="no-withdrawals">No withdrawals yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.withdrawals.map((w, i) => (
                  <TableRow key={i} data-testid={`withdrawal-row-${i}`}>
                    <TableCell className="text-sm">
                      {new Date(w.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      ${w.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {(w.payment_method || "-").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`border-0 ${
                        w.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                        w.status === "pending" ? "bg-amber-50 text-amber-700" :
                        w.status === "rejected" ? "bg-red-50 text-red-700" :
                        "bg-emerald-50 text-emerald-700"
                      }`}>
                        {w.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Analytics Section */}
      {analytics && (
        <div className="mt-8 space-y-6" data-testid="analytics-section">
          <h2 className="font-heading text-xl font-bold tracking-tight">Page Analytics</h2>
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-border rounded-sm card-hover">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Eye className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Total Views</p>
                    <p className="font-heading text-2xl font-bold" data-testid="analytics-total-views">{analytics.total_views}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border rounded-sm card-hover">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-violet-50 flex items-center justify-center">
                    <UsersIcon className="h-4 w-4 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Unique Visitors</p>
                    <p className="font-heading text-2xl font-bold" data-testid="analytics-unique-visitors">{analytics.unique_visitors}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border rounded-sm card-hover">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">Today</p>
                    <p className="font-heading text-2xl font-bold" data-testid="analytics-today-views">{analytics.today_views}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Views Chart */}
          {analytics.daily_views?.length > 0 && (
            <Card className="border-border rounded-sm" data-testid="analytics-chart">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">Views (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[220px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.daily_views} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                      <defs>
                        <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                      <Area type="monotone" dataKey="views" stroke="#059669" strokeWidth={2} fill="url(#viewsGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Referrers */}
          {analytics.top_referrers?.length > 0 && (
            <Card className="border-border rounded-sm" data-testid="analytics-referrers">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">Top Referrers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analytics.top_referrers.map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-muted-foreground truncate max-w-xs">{r.referrer}</span>
                      <Badge variant="secondary" className="ml-2">{r.count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Withdraw Dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="rounded-sm" data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Withdraw Funds</DialogTitle>
            <DialogDescription>Enter the amount you wish to withdraw. Requires admin approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={balance}
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="withdraw-amount-input"
              />
              <p className="text-xs text-muted-foreground">
                Available: ${balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </p>
            </div>
            {/* Payment Info */}
            {(dashboard?.payment_method || user?.payment_method) ? (
              <div className="p-3 bg-muted rounded-sm" data-testid="withdraw-payment-info">
                <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground mb-1">Payment Method</p>
                <p className="text-sm font-medium">
                  {(dashboard?.payment_method || user?.payment_method || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </p>
                <p className="text-sm text-muted-foreground mt-0.5 break-all">
                  {dashboard?.payment_address || user?.payment_address || "No address set"}
                </p>
              </div>
            ) : (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="withdraw-no-payment">
                <p className="text-xs text-amber-700">No payment method set. Please configure in Settings before withdrawing.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              onClick={handleWithdraw}
              disabled={withdrawLoading}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="withdraw-submit-btn"
            >
              {withdrawLoading ? "Processing..." : "Withdraw"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
